import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

// import { ReactComponent as ChatsIcon } from '@/assets/icons/chats.svg';
import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';

import { SectionHeading } from '../../components/Layout/Parts';
import CommentAndReplies from './components/CommentAndReplies';
import CreateCommentModal from './components/CreateCommentModal';
import InfoBox from './components/InfoBox';
import ListPlaceholder from './components/ListPlaceholder';

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledButton = styled(Button)`
  padding: 0 32px;
`;

// const MoreButtonHolder = styled.div`
//   text-align: center;
//   margin-top: 24px;
// `;

// const MoreButton = styled(Button)`
//   background-color: transparent;
//   border-radius: 50px;
//   height: 46px;
//   font-size: 14px;
//   line-height: 22px;
//   padding: 0 24px;
//   gap: 8px;
//   &:hover,
//   &:active {
//     background-color: ${({ theme }) => theme.colors.grayscale};
//   }
// `;

const EditorialDistributionSingle = () => {
  const { id } = useParams();
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data: post } = useQuery(
    [
      'editorial_distribution_schedules',
      {
        id,
      },
    ],
    () =>
      api.get<never, EditorialFacebookPostResource>(
        `/v1/editorial/facebook-posts/${id}`,
      ),
  );

  const onGetComments = async () => {
    try {
      if (post?.fbComments?.length) {
        const confirmed = await alerts.confirm(
          'Are you sure you want to refetch all comments?',
        );

        if (!confirmed) {
          return;
        }
      }

      await api.post(`/v1/editorial/facebook-posts/${id}/comments`);

      await queryClient.invalidateQueries([
        'editorial_distribution_schedules',
        {
          id,
        },
      ]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  if (!post) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <HeadingsRow>
        <PageHeading>Post Distribution</PageHeading>
        <Actions>
          <StyledButton color="secondary" onClick={() => setShowCreate(true)}>
            Create comment
          </StyledButton>
          <StyledButton
            onClick={onGetComments}
            disabled={post.isGeneratingComments}
            color={post.fbComments?.length ? 'danger' : 'primary'}
          >
            {post.fbComments?.length ? 'Refetch comments' : 'Get comments'}
          </StyledButton>
        </Actions>
      </HeadingsRow>
      <BackButton />
      <InfoBox post={post!} />
      <SectionHeading>comments & replies</SectionHeading>
      {!post.fbComments?.length && <ListPlaceholder />}
      {post.isGeneratingComments && <ListPlaceholder isLoading />}
      {!!post.fbComments?.length && (
        <>
          {post.fbComments.map((c, index) => (
            <CommentAndReplies key={c.id} comment={c} no={index + 1} />
          ))}
        </>
      )}
      {showCreate && (
        <CreateCommentModal onClose={() => setShowCreate(false)} />
      )}
      {/* <MoreButtonHolder>
        <MoreButton color="secondary" disabled>
          <ChatsIcon />
          Get more comments & generate replies
        </MoreButton>
      </MoreButtonHolder> */}
    </>
  );
};

export default EditorialDistributionSingle;
